<template>
  <dashboard-layout title="User Dashboard">
    <template #actions>
      <search-box
        v-model="searchByUid"
        placeholder="Search by User ID..."
        @update:model-value="handleUidSearch"
      />
      <search-box
        v-model="searchByName"
        placeholder="Search by Name..."
        @update:model-value="handleNameSearch"
      />
      <span v-if="isSearching" class="search-spinner" title="Searching…"></span>
      <div v-if="!isDeleteModeActive && !isExportModeActive" class="header-buttons">
        <button class="action-button" @click="enterDeleteMode">
          Delete Users
        </button>
        <button class="action-button" @click="enterExportMode">
          Export Users
        </button>
      </div>
      <div v-if="isDeleteModeActive" class="delete-actions">
        <button class="cancel-button" @click="exitDeleteMode">
          Cancel
        </button>
        <button
          class="select-all-button"
          @click="toggleSelectAllVisible"
          :disabled="filteredUsers.length === 0"
        >
          {{ allVisibleSelected ? 'Deselect All' : 'Select All' }}
        </button>
        <button
          class="delete-confirm-button"
          @click="confirmDeleteSelectedUsers"
          :disabled="selectedUsers.length === 0">
          Delete ({{ selectedUsers.length }})
        </button>
      </div>
      <div v-if="isExportModeActive" class="export-actions">
        <button class="cancel-button" @click="exitExportMode">
          Cancel
        </button>
        <button
          class="select-all-button"
          @click="toggleSelectAllVisible"
          :disabled="filteredUsers.length === 0"
        >
          {{ allVisibleSelected ? 'Deselect All' : 'Select All' }}
        </button>
        <button
          class="export-confirm-button"
          @click="exportSelectedUsers"
          :disabled="selectedUsers.length === 0">
          Export ({{ selectedUsers.length }})
        </button>
      </div>
    </template>

    <stat-grid>
      <stat-card label="Loaded users" :value="users.length.toLocaleString('en-US')" icon="users" accent />
      <stat-card label="With chat" :value="withChatCount.toLocaleString('en-US')" icon="messagecircle" />
      <stat-card label="With birth chart" :value="withBirthChartCount.toLocaleString('en-US')" icon="activity" />
    </stat-grid>

    <data-panel flush>
      <template #title>Users <span class="muted">({{ sortedUsers.length.toLocaleString('en-US') }})</span></template>
      <template #actions>
        <filter-chips v-model="chatFilter" :options="chatFilterOptions" />
      </template>

      <loading-state v-if="loading && users.length === 0" label="Loading users..." />

      <div v-else class="table-scroll-wrapper" @scroll="handleScroll" ref="tableScrollWrapper">
        <table class="users-table">
          <thead>
            <tr>
              <th @click="sortBy('name')" class="sortable">Name <span class="sort-arrow" v-if="sortKey === 'name'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
              <th @click="sortBy('birthDate')" class="sortable">Birth Date <span class="sort-arrow" v-if="sortKey === 'birthDate'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
              <th @click="sortBy('birthTime')" class="sortable">Birth Time <span class="sort-arrow" v-if="sortKey === 'birthTime'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
              <th @click="sortBy('birthPlace')" class="sortable">Birth Place <span class="sort-arrow" v-if="sortKey === 'birthPlace'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
              <th @click="sortBy('gender')" class="sortable">Gender <span class="sort-arrow" v-if="sortKey === 'gender'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
              <th @click="sortBy('sunSign')" class="sortable">Sun Sign <span class="sort-arrow" v-if="sortKey === 'sunSign'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
              <th @click="sortBy('createdAt')" class="sortable">Created At <span class="sort-arrow" v-if="sortKey === 'createdAt'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
              <th>Chat</th>
              <th>BC</th>
              <th @click="sortBy('device')" class="sortable">Device <span class="sort-arrow" v-if="sortKey === 'device'">{{ sortDirection === 1 ? '▲' : '▼' }}</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in sortedUsers" :key="user.uid"
                @click="handleRowClick(user, $event)"
                :class="['user-row', { 'selected-row': (isDeleteModeActive || isExportModeActive) && selectedUsers.includes(user.uid) }]">
              <td>
                <div class="user-info">
                  <div class="user-avatar">{{ getUserInitials(user.properties?.name) }}</div>
                  <span>{{ formatName(user.properties?.name) }}</span>
                </div>
              </td>
              <td>{{ formatBirthDate(user.properties) }}</td>
              <td>{{ formatBirthTime(user.properties) }}</td>
              <td>{{ formatBirthPlace(user.properties?.guessCountry) }}</td>
              <td>{{ formatGender(user.properties?.gender) }}</td>
              <td>{{ user.astrology?.planets?.[0]?.zodiac || '-' }}</td>
              <td>{{ formatCreatedAt(user.createdAt) }}</td>
              <td class="chat-status-cell">
                <span v-if="chatStatusMap[user.uid] === undefined" class="chat-checking"></span>
                <span v-else-if="chatStatusMap[user.uid]" class="chat-indicator has-chat" title="Has chat messages">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
                  <span class="chat-count">{{ chatCountMap[user.uid] }}</span>
                </span>
                <span v-else class="chat-indicator no-chat">—</span>
              </td>
              <td><span v-if="user.birthChartV2" class="bc-badge" title="Birth Chart available">&#9733;</span><span v-else class="bc-none">—</span></td>
              <td>{{ formatDevice(user.properties?.isWeb) }}</td>
            </tr>
            <tr v-if="sortedUsers.length === 0 && !loading">
              <td colspan="10" class="no-data">No users found</td>
            </tr>
          </tbody>
        </table>
        <div v-if="loadingMore" class="loading-more">
          <div class="spinner"></div>
          <span>Loading more users...</span>
        </div>
        <div v-if="!hasMore && sortedUsers.length > 0" class="end-message">
          No more users to load
        </div>
      </div>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { FirebaseUserService } from '../services/firebaseUserService'
import { FirebaseChatService } from '../services/firebaseChatService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'

export default {
  name: 'UserDashboardComponent',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, SearchBox, FilterChips, LoadingState
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const userService = new FirebaseUserService()
    const chatService = new FirebaseChatService()
    const users = ref([])
    const filteredUsers = ref([])
    const searchByUid = ref('')
    const searchByName = ref('')
    const loading = ref(true)
    const loadingMore = ref(false)
    const hasMore = ref(true)
    const lastDoc = ref(null)
    const tableScrollWrapper = ref(null)
    const sortKey = ref('createdAt')
    const sortDirection = ref(-1)
    const selectedUsers = ref([])
    const isDeleteModeActive = ref(false)
    const isExportModeActive = ref(false)
    const isSearching = ref(false)
    const searchTimeout = ref(null)
    const chatStatusMap = ref({})
    const chatFilter = ref('all')
    const dailyStats = ref({
      newUsers: [],
      newChats: []
    })

    const chatCountMap = ref({})

    const checkChatStatusForUsers = async (userList) => {
      const unchecked = userList.filter(u => !(u.uid in chatStatusMap.value))
      if (unchecked.length === 0) return

      const results = await Promise.allSettled(
        unchecked.map(async (user) => {
          const count = await chatService.getUserChatCount(user.uid)
          return { uid: user.uid, count }
        })
      )

      const newStatusMap = { ...chatStatusMap.value }
      const newCountMap = { ...chatCountMap.value }
      results.forEach(result => {
        if (result.status === 'fulfilled') {
          newStatusMap[result.value.uid] = result.value.count > 0
          newCountMap[result.value.uid] = result.value.count
        }
      })
      chatStatusMap.value = newStatusMap
      chatCountMap.value = newCountMap
    }

    watch(() => route.name, (routeName) => {
      if (routeName === 'dashboard') {
        loadUsers()
      }
    })

    const enterDeleteMode = () => {
      isDeleteModeActive.value = true
      isExportModeActive.value = false
    }

    const exitDeleteMode = () => {
      isDeleteModeActive.value = false
      selectedUsers.value = []
    }

    const enterExportMode = () => {
      isExportModeActive.value = true
      isDeleteModeActive.value = false
    }

    const exitExportMode = () => {
      isExportModeActive.value = false
      selectedUsers.value = []
    }

    const loadUsers = async () => {
      loading.value = true
      try {
        const result = await userService.getUsersPaginated(40)
        users.value = result.users.filter(user => !user.isDeletedFromDashboard)
        lastDoc.value = result.lastVisible
        hasMore.value = result.hasMore
        filteredUsers.value = users.value
        await checkChatStatusForUsers(users.value)
        ensureEnoughVisibleUsers()

        const stats = calculateDailyStats(users.value, 7)
        dailyStats.value = stats
      } catch (error) {
        console.error('Error loading users:', error)
      } finally {
        loading.value = false
      }
    }

    const loadMore = async () => {
      if (loadingMore.value || !hasMore.value) return

      loadingMore.value = true
      try {
        const result = await userService.getUsersPaginated(40, lastDoc.value)
        const newUsers = result.users.filter(user => !user.isDeletedFromDashboard)
        users.value = [...users.value, ...newUsers]
        filteredUsers.value = users.value
        lastDoc.value = result.lastVisible
        hasMore.value = result.hasMore
        await checkChatStatusForUsers(newUsers)
      } finally {
        loadingMore.value = false
      }
    }

    const handleScroll = (e) => {
      const element = e.target
      const scrollPercentage = (element.scrollTop + element.clientHeight) / element.scrollHeight

      if (scrollPercentage > 0.8 && !loadingMore.value && hasMore.value) {
        loadMore()
      }
    }

    const calculateDailyStats = (users, days) => {
      const newUsers = []
      const newChats = []

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date()
        date.setDate(date.getDate() - i)
        const dateStr = date.toLocaleDateString('en-CA')

        const dayUsers = users.filter(user => {
          const userDate = user.createdAt?.toDate?.() || new Date(user.createdAt)
          return userDate.toLocaleDateString('en-CA') === dateStr
        }).length

        const dayChats = users.filter(user => {
          const lastMessage = user.lastMessageAt?.toDate?.() || new Date(user.lastMessageAt)
          return lastMessage.toLocaleDateString('en-CA') === dateStr
        }).length

        newUsers.push({ date: dateStr, value: dayUsers })
        newChats.push({ date: dateStr, value: dayChats })
      }

      return { newUsers, newChats }
    }

    const formatMessageTime = (timestamp) => {
      if (!timestamp) return ''
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    }

    const performUidSearch = async () => {
      const uid = searchByUid.value.trim()

      if (uid.length === 0) {
        if (searchByName.value.trim().length === 0) {
          await loadUsers()
        }
        return
      }

      if (uid.length < 3) {
        return
      }

      isSearching.value = true
      try {
        const result = await userService.searchUserByUid(uid)
        users.value = result.users.filter(user => !user.isDeletedFromDashboard)
        filteredUsers.value = users.value
        hasMore.value = false
        lastDoc.value = null
        searchByName.value = ''
        checkChatStatusForUsers(users.value)
      } catch (error) {
        console.error('Error searching user by UID:', error)
      } finally {
        isSearching.value = false
      }
    }

    const performNameSearch = async () => {
      const name = searchByName.value.trim()

      if (name.length === 0) {
        if (searchByUid.value.trim().length === 0) {
          await loadUsers()
        }
        return
      }

      if (name.length < 2) {
        return
      }

      isSearching.value = true
      try {
        const result = await userService.searchUserByName(name, 100)
        users.value = result.users.filter(user => !user.isDeletedFromDashboard)
        filteredUsers.value = users.value
        hasMore.value = false
        lastDoc.value = null
        searchByUid.value = ''
        checkChatStatusForUsers(users.value)
      } catch (error) {
        console.error('Error searching user by name:', error)
      } finally {
        isSearching.value = false
      }
    }

    const handleUidSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }

      searchTimeout.value = setTimeout(() => {
        performUidSearch()
      }, 500)
    }

    const handleNameSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }

      searchTimeout.value = setTimeout(() => {
        performNameSearch()
      }, 500)
    }

    const sortedUsers = computed(() => {
      let list = [...filteredUsers.value]

      if (chatFilter.value === 'has-chat') {
        list = list.filter(u => chatStatusMap.value[u.uid] === true)
      } else if (chatFilter.value === 'no-chat') {
        list = list.filter(u => chatStatusMap.value[u.uid] === false)
      }

      const sorted = list
      if (sortKey.value) {
        sorted.sort((a, b) => {
          let valA, valB

          switch (sortKey.value) {
            case 'name':
              valA = a.properties?.name || ''
              valB = b.properties?.name || ''
              break
            case 'birthDate':
              valA = a.properties?.year ? new Date(a.properties.year, a.properties.month - 1, a.properties.date).getTime() : 0
              valB = b.properties?.year ? new Date(b.properties.year, b.properties.month - 1, b.properties.date).getTime() : 0
              break
            case 'birthTime':
              valA = (a.properties?.hours ?? -1) * 60 + (a.properties?.minutes ?? 0)
              valB = (b.properties?.hours ?? -1) * 60 + (b.properties?.minutes ?? 0)
              break
            case 'birthPlace':
              valA = a.properties?.guessCountry || ''
              valB = b.properties?.guessCountry || ''
              break
            case 'gender':
              valA = a.properties?.gender || ''
              valB = b.properties?.gender || ''
              break
            case 'sunSign':
              valA = a.astrology?.planets?.[0]?.zodiac || ''
              valB = b.astrology?.planets?.[0]?.zodiac || ''
              break
            case 'createdAt':
              valA = a.createdAt?.seconds || 0
              valB = b.createdAt?.seconds || 0
              break
            case 'questionCount':
              valA = a.questionCount || 0
              valB = b.questionCount || 0
              break
            case 'lastMessageAt':
              valA = a.lastMessageAt?.seconds || 0
              valB = b.lastMessageAt?.seconds || 0
              break
            case 'device':
              valA = a.properties?.isWeb ? 'Web' : 'Mobile'
              valB = b.properties?.isWeb ? 'Web' : 'Mobile'
              break
            default:
              return 0
          }

          if (typeof valA === 'string') {
            return valA.localeCompare(valB) * sortDirection.value
          }
          return (valA - valB) * sortDirection.value
        })
      }
      return sorted
    })

    const ensureEnoughVisibleUsers = async () => {
      if (chatFilter.value === 'all') return
      while (hasMore.value && sortedUsers.value.length < 20) {
        await loadMore()
      }
    }

    watch(chatFilter, () => {
      ensureEnoughVisibleUsers()
    })

    const sortBy = (key) => {
      if (sortKey.value === key) {
        sortDirection.value *= -1
      } else {
        sortKey.value = key
        sortDirection.value = 1
      }
    }

    const getUserInitials = (name) => {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    }

    const formatName = (name) => {
      if (!name) return '-'
      if (name.length > 15) {
        return name.substring(0, 15) + '...'
      }
      return name
    }

    const formatBirthDate = (properties) => {
      if (!properties) return '-'
      const { date, month, year } = properties
      if (!date || !month || !year) return '-'
      return `${date}/${month}/${year}`
    }

    const formatBirthTime = (properties) => {
      if (!properties) return '-'
      const { hours, minutes } = properties
      if (hours === undefined || minutes === undefined) return '-'
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    }

    const formatGender = (gender) => {
      if (!gender) return '-'
      return gender.charAt(0).toUpperCase() + gender.slice(1).replace('_', ' ')
    }

    const formatCreatedAt = (timestamp) => {
      if (!timestamp) return '-'
      const date = new Date(timestamp.seconds * 1000)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatLastMessageDate = (timestamp) => {
      if (!timestamp) return 'No messages'
      const now = new Date()
      const date = new Date(timestamp.seconds * 1000)
      const seconds = Math.floor((now - date) / 1000)

      if (seconds < 60) {
        return `${seconds} second${seconds === 1 ? '' : 's'} ago`
      } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60)
        return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
      } else if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600)
        return `${hours} hour${hours === 1 ? '' : 's'} ago`
      } else if (seconds < 2592000) { // 30 days
        const days = Math.floor(seconds / 86400)
        return `${days} day${days === 1 ? '' : 's'} ago`
      } else {
        return date.toLocaleString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    }

    const formatBirthPlace = (place) => {
      if (!place) return '-'
      if (place.length > 20) {
        return place.substring(0, 20) + '...'
      }
      return place
    }

    const formatDevice = (isWeb) => {
      if (isWeb === undefined) return 'iOS'
      return isWeb ? 'Web' : 'Mobile'
    }

    const withChatCount = computed(() => users.value.filter(u => chatStatusMap.value[u.uid] === true).length)
    const withoutChatCount = computed(() => users.value.filter(u => chatStatusMap.value[u.uid] === false).length)
    const withBirthChartCount = computed(() => users.value.filter(u => u.birthChartV2).length)
    const chatFilterOptions = computed(() => ([
      { value: 'all', label: 'All Users', count: users.value.length },
      { value: 'has-chat', label: 'Has Chat', count: withChatCount.value },
      { value: 'no-chat', label: 'No Chat', count: withoutChatCount.value }
    ]))

    const allVisibleSelected = computed(() => {
      const visibleIds = new Set(filteredUsers.value.map(u => u.uid));
      if (visibleIds.size === 0) return false;
      for (const id of visibleIds) {
          if (!selectedUsers.value.includes(id)) {
              return false;
          }
      }
      return true;
    });

    const toggleSelectAllVisible = () => {
        const visibleIds = filteredUsers.value.map(u => u.uid);
        if (allVisibleSelected.value) {
            // Deselect all visible
            selectedUsers.value = selectedUsers.value.filter(uid => !visibleIds.includes(uid));
        } else {
            // Select all visible (add to current selection)
            const currentSelection = new Set(selectedUsers.value);
            visibleIds.forEach(uid => currentSelection.add(uid));
            selectedUsers.value = Array.from(currentSelection);
        }
    };

    const toggleSelectUser = (uid) => {
      const index = selectedUsers.value.indexOf(uid)
      if (index > -1) {
        selectedUsers.value.splice(index, 1)
      } else {
        selectedUsers.value.push(uid)
      }
    }

    const confirmDeleteSelectedUsers = () => {
      if (selectedUsers.value.length === 0) return

      const confirmation = confirm(`Are you sure you want to delete ${selectedUsers.value.length} selected user(s)? This action cannot be undone.`)

      if (confirmation) {
        deleteSelectedUsers()
      }
    }

    const deleteSelectedUsers = async () => {
      try {
        loading.value = true
        await Promise.all(selectedUsers.value.map(uid => userService.updateUser(uid, { isDeletedFromDashboard: true })))
        alert(`${selectedUsers.value.length} user(s) successfully marked as deleted from dashboard.`)
        await loadUsers()
        exitDeleteMode()
      } catch (error) {
        console.error("Error deleting users:", error)
        alert("Failed to delete users.")
      } finally {
        loading.value = false
      }
    }

    const exportSelectedUsers = async () => {
      if (selectedUsers.value.length === 0) return;

      try {
        loading.value = true;
        const usersToExport = users.value.filter(u => selectedUsers.value.includes(u.uid));

        const toCsvRow = (fields) => {
          return fields.map(field => {
            const str = String(field === null || field === undefined ? '' : field);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          }).join(',');
        };

        const headers = [
          "User ID", "Name", "Birth Date", "Birth Time", "Birth Place",
          "Gender", "Sun Sign", "Created At", "Question Count", "Device",
          "Question"
        ];
        let csvRows = [headers.join(",")];

        for (const user of usersToExport) {
          const chatHistory = (await chatService.getUserChatHistory(user.uid, true)).reverse();
          const userMessages = chatHistory.filter(msg => msg.role === 'user');

          if (userMessages.length === 0) {
            continue;
          }

          for (const message of userMessages) {
            const userData = [
              user.uid,
              user.properties?.name || '-',
              formatBirthDate(user.properties),
              formatBirthTime(user.properties),
              user.properties?.guessCountry || '-',
              formatGender(user.properties?.gender),
              user.astrology?.planets?.[0]?.zodiac || '-',
              formatCreatedAt(user.createdAt),
              user.questionCount,
              formatDevice(user.properties?.isWeb),
              message.content
            ];
            csvRows.push(toCsvRow(userData));
          }
        }

        const csvContent = csvRows.join("\r\n");
        const encodedUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);

        const now = new Date();
        const dateString = now.toISOString().slice(0, 10);
        const timeString = now.toTimeString().slice(0, 8).replace(/:/g, '-');
        const fileName = `user_export_${dateString}_${timeString}.csv`;

        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        exitExportMode();

      } catch (error) {
        console.error("Error exporting users:", error);
        alert("Failed to export users.");
      } finally {
        loading.value = false;
      }
    };

    const openUserDetail = (uid, event) => {
      if (event.metaKey || event.ctrlKey) {
        window.open(`/user/${uid}`, '_blank')
      } else {
        router.push(`/user/${uid}`)
      }
    }

    const handleRowClick = (user, event) => {
      if (isDeleteModeActive.value || isExportModeActive.value) {
        toggleSelectUser(user.uid)
      } else {
        openUserDetail(user.uid, event)
      }
    }

    onMounted(() => {
      loadUsers()
    })

    onUnmounted(() => {
    })

    return {
      users,
      sortedUsers,
      searchByUid,
      searchByName,
      loading,
      loadingMore,
      hasMore,
      isSearching,
      tableScrollWrapper,
      handleUidSearch,
      handleNameSearch,
      getUserInitials,
      formatBirthDate,
      formatBirthTime,
      formatGender,
      formatCreatedAt,
      formatLastMessageDate,
      formatBirthPlace,
      formatName,
      sortBy,
      sortKey,
      sortDirection,
      formatDevice,
      selectedUsers,
      toggleSelectUser,
      confirmDeleteSelectedUsers,
      deleteSelectedUsers,
      exportSelectedUsers,
      isDeleteModeActive,
      enterDeleteMode,
      exitDeleteMode,
      isExportModeActive,
      enterExportMode,
      exitExportMode,
      handleRowClick,
      allVisibleSelected,
      toggleSelectAllVisible,
      filteredUsers,
      chatStatusMap,
      chatCountMap,
      chatFilter,
      chatFilterOptions,
      withChatCount,
      withBirthChartCount,
      formatMessageTime,
      dailyStats,
      handleScroll
    }
  },
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.search-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(108, 99, 255, 0.3);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}
.header-buttons {
  display: flex;
  gap: 10px;
}
.action-button {
  padding: 8px 14px;
  background: var(--background-secondary);
  border: 1px solid var(--background-tertiary);
  border-radius: 8px;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 13px;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.action-button:hover {
  background: var(--background-tertiary);
  border-color: var(--primary-color);
}
.table-scroll-wrapper {
  overflow: auto;
  max-height: calc(100vh - 250px);
}
.users-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
}
.users-table th,
.users-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  white-space: nowrap;
}
.users-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(26, 27, 46, 0.98);
  height: 44px;
}
.users-table th.sortable {
  cursor: pointer;
  transition: color 0.2s ease;
  user-select: none;
}
.users-table th.sortable:hover {
  color: var(--text-primary);
}
.users-table th .sort-arrow {
  font-size: 10px;
  margin-left: 4px;
  display: inline-block;
  opacity: 0.8;
}
.users-table td {
  color: var(--text-primary);
  font-size: 13px;
}
.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: white;
  flex-shrink: 0;
}
.no-data {
  text-align: center;
  color: var(--text-secondary);
  padding: 32px !important;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.user-row {
  cursor: pointer;
  transition: background-color 0.3s ease;
}
.user-row:hover {
  background: rgba(255, 255, 255, 0.05);
}
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  color: var(--text-secondary);
  font-size: 14px;
}
.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(108, 99, 255, 0.3);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 0.8s linear infinite;
}
.end-message {
  text-align: center;
  padding: 24px;
  color: var(--primary-color);
  font-size: 13px;
  font-weight: 500;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.chat-status-cell {
  text-align: center;
  width: 50px;
}
.chat-checking {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(108, 99, 255, 0.3);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 0.8s linear infinite;
}
.chat-indicator.has-chat {
  color: #22c55e;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.chat-count {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255,255,255,0.5);
}
.chat-indicator.no-chat {
  color: var(--text-secondary);
  opacity: 0.4;
}
.bc-badge { color: #fbbf24; font-size: 14px; }
.bc-none { color: var(--text-secondary); opacity: 0.4; }
.selected-row {
  background-color: rgba(108, 99, 255, 0.2) !important;
  box-shadow: inset 3px 0 0 0 var(--primary-color);
}
.delete-actions, .export-actions {
  display: flex;
  gap: 10px;
}
.cancel-button, .select-all-button, .delete-confirm-button, .export-confirm-button {
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid var(--background-tertiary);
}
.cancel-button {
  background: var(--background-secondary);
  color: var(--text-primary);
}
.cancel-button:hover {
  background: var(--background-tertiary);
}
.select-all-button {
  background: rgba(108, 99, 255, 0.1);
  color: var(--primary-color);
  border-color: var(--primary-color);
}
.select-all-button:hover {
  background: rgba(108, 99, 255, 0.2);
}
.select-all-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--background-secondary);
  color: var(--text-secondary);
  border-color: var(--background-tertiary);
}
.delete-confirm-button {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  border-color: #ef4444;
}
.delete-confirm-button:hover {
  background: rgba(239, 68, 68, 0.3);
}
.delete-confirm-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--background-secondary);
  color: var(--text-secondary);
  border-color: var(--background-tertiary);
}
.export-confirm-button {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
  border-color: #22c55e;
}
.export-confirm-button:hover {
  background: rgba(34, 197, 94, 0.3);
}
.export-confirm-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--background-secondary);
  color: var(--text-secondary);
  border-color: var(--background-tertiary);
}
@media (max-width: 1024px) {
  .table-scroll-wrapper {
    max-height: calc(100vh - 340px);
  }
  .delete-actions, .export-actions, .header-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .delete-actions .select-all-button,
  .export-actions .select-all-button {
    grid-column: 1 / -1;
  }
}
</style>
